<template>
  <div class="iframe-container">
    <div v-if="iframeList.length > 1" class="iframe-toggle">
      <div class="iframe-capsule">
        <div
          v-for="(item, idx) in iframeList"
          :key="idx"
          class="iframe-capsule-option"
          :class="{ active: activeIndex === idx }"
          @click="switchTab(idx)"
        >
          {{ item.name }}
        </div>
      </div>
    </div>
    <div
      v-for="item in iframeList"
      :key="item.url"
      class="iframe-frame"
      :class="{ hidden: activeIndex !== iframeList.indexOf(item) }"
    >
      <div v-if="!iframesReady.has(item.url)" class="iframe-frame__loading">
        <img class="iframe-frame__loading-img" :src="loadingGif" alt="loading" />
      </div>
      <IframeView
        :url="item.url"
        :ref="'ifv-' + item.url"
        @loaded="onIframeLoad(item.url)"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import loadingGif from '@/assets/cosmo_loading.gif'

/**
 * 子组件：仅在 mounted 时设置一次 iframe src，避免 v-for patch 误触 src 导致重载
 */
const IframeView = {
  name: 'IframeView',
  props: {
    url: { type: String, required: true }
  },
  render(h) {
    return h('iframe', {
      ref: 'iframe',
      attrs: { frameborder: '0' },
      on: { load: () => this.$emit('loaded') }
    })
  },
  mounted() {
    this.$refs.iframe.src = this.url
  },
  methods: {
    getIframe() {
      return this.$refs.iframe
    }
  }
}

export default {
  name: 'IframeContainer',
  components: { IframeView },
  data() {
    return {
      loadingGif,
      // 持久化 iframe 列表：追加不删除，只通过隐藏控制显示
      iframeList: [],
      pendingMessage: null,
      iframesReady: new Set()
    }
  },
  computed: {
    ...mapState('cosmoClaw', ['iframeItems', 'iframeActiveIndex', 'postMessagePayload']),
    activeIndex() {
      return this.iframeActiveIndex
    }
  },
  watch: {
    iframeItems: {
      immediate: true,
      handler(newItems) {
        if (!newItems || newItems.length === 0) {
          this.iframeList = []
          this.pendingMessage = null
          this.iframesReady = new Set()
          return
        }

        // 只追加不删除：已有的 iframe 保留，新增的追加
        const existingUrls = new Set(this.iframeList.map(item => item.url))
        let changed = false
        for (const item of newItems) {
          if (!existingUrls.has(item.url)) {
            this.iframeList.push({ ...item })
            existingUrls.add(item.url)
            changed = true
          } else {
            // 更新名称
            const existing = this.iframeList.find(r => r.url === item.url)
            if (existing && existing.name !== item.name) {
              existing.name = item.name
              changed = true
            }
          }
        }

        // 路由切换后重新定位激活项：列表只追加不删除，目标 iframe 可能不在 index 0
        // （如先访问知识库、再访问数字人广场，广场 iframe 被追加到末尾）。
        // 若当前激活项不属于新 items，则激活新 items 第一项对应的 iframe。
        const targetUrls = new Set(newItems.map((i) => i.url))
        const currentActive = this.iframeList[this.iframeActiveIndex]
        const activeMatchesTarget =
          currentActive && targetUrls.has(currentActive.url)
        if (!activeMatchesTarget) {
          // 设置方统一以 activeIndex=0 表示激活新 items 的第一个，这里映射到其在累积列表中的真实索引
          const firstItem = newItems[0]
          const targetIdx = this.iframeList.findIndex(
            (r) => r.url === firstItem.url
          )
          if (targetIdx >= 0) {
            this.$store.commit('cosmoClaw/SET_IFRAME_ACTIVE_INDEX', targetIdx)
          }
        } else if (this.iframeActiveIndex >= this.iframeList.length) {
          this.$store.commit('cosmoClaw/SET_IFRAME_ACTIVE_INDEX', 0)
        }

        // iframe 列表更新后，尝试发送待发消息
        // 避免 postMessagePayload 先于 iframeItems 到达时消息丢失
        if (changed && this.pendingMessage) {
          this._sendWhenAllReady()
        }
      }
    },
    postMessagePayload(payload) {
      if (!payload) return
      this.pendingMessage = { type: payload.type, data: payload.data }
      this._sendWhenAllReady()
    }
  },
  mounted() {
    window.addEventListener('message', this._handlePostMessage)
  },
  beforeDestroy() {
    window.removeEventListener('message', this._handlePostMessage)
  },
  methods: {
    switchTab(idx) {
      this.$store.commit('cosmoClaw/SET_IFRAME_ACTIVE_INDEX', idx)
    },
    onIframeLoad(url) {
      this.iframesReady = new Set(this.iframesReady).add(url)
      if (this.pendingMessage) {
        this._sendWhenAllReady()
      }
    },
    _sendWhenAllReady() {
      this.$nextTick(() => {
        if (!this.pendingMessage) {
          return
        }
        // 收集当前可见（iframeItems 中有）的所有 iframe URL
        const targetUrls = new Set((this.iframeItems || []).map(i => i.url))
        if (targetUrls.size === 0) {
          return
        }

        // 检查是否所有目标 iframe 都已就绪
        const allReady = [...targetUrls].every(url => this.iframesReady.has(url))
        if (!allReady) return

        // 全部就绪，统一发送
        const iframeArr = this._getAllIframes()
        iframeArr.forEach((iframe, idx) => {
          if (iframe && iframe.contentWindow) {
            try {
              const payload = { type: this.pendingMessage.type, data: JSON.stringify(this.pendingMessage.data) }
              iframe.contentWindow.postMessage(payload, '*')
            } catch (e) {
              console.warn('[IframeContainer] postMessage 失败:', e)
            }
          } else {
            console.warn('[IframeContainer] iframe[' + idx + '] contentWindow 不可用')
          }
        })

        this.pendingMessage = null
      })
    },
    _getAllIframes() {
      return this.iframeList
        .map(item => {
          const ref = this.$refs['ifv-' + item.url]
          if (!ref) return null
          if (Array.isArray(ref)) return ref[0]?.getIframe ? ref[0].getIframe() : ref[0]
          return ref.getIframe ? ref.getIframe() : ref
        })
        .filter(Boolean)
    },
    _handlePostMessage(event) {
      const msg = event.data
      console.log('[IframeContainer] 收到消息:', msg)
      if (!msg) return

      if (msg.type === 'TYPE_CLAW_BROADCAST') {
        // 走 pending 机制，等所有 iframe 就绪后再广播
        this.pendingMessage = { type: 'TYPE_CLAW_SEND', data: JSON.parse(msg.data) }
        console.log('[IframeContainer] 等待所有 iframe 就绪...', this.pendingMessage)
        this._sendWhenAllReady()
        return
      }

      if (msg.type === 'TYPE_CLAW_RECEIVE' && msg.data) {
        this.$store.dispatch('cosmoClaw/sendSilentMessage', {
          content: '[iframe-response]' + JSON.stringify(msg.data)
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.iframe-container {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.iframe-toggle {
  position: absolute;
  top: 12px;
  right: 16px;
  z-index: 10;
}

.iframe-capsule {
  display: flex;
  align-items: center;
  background: #f7f8fc;
  border-radius: 4px;
  user-select: none;
}

.iframe-capsule .iframe-capsule-option {
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #4e5969;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;

  &.active {
    background: #4a6cf7;
    color: #fff;
    box-shadow: 0 2px 6px rgba(74, 108, 247, 0.35);
  }

  &:not(.active):hover {
    color: #1d2129;
    background: #f7f8fa;
  }
}

.iframe-frame {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;

  &.hidden {
    display: none;
  }

  iframe {
    flex: 1;
    width: 100%;
    height: 100%;
    border: none;
  }
}

.iframe-frame__loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
}

.iframe-frame__loading-img {
  width: 282px;
  object-fit: contain;
}
</style>

<style lang="scss">
.iframe-frame iframe::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
.iframe-frame iframe::-webkit-scrollbar-track {
  background: transparent;
  box-shadow: none;
}
.iframe-frame iframe::-webkit-scrollbar-thumb {
  background: #FFFFFF;
  border-radius: 8px;
  box-shadow: none;
}
</style>
